import {type SubmitHandler, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';
import dayjs from 'dayjs';
import {
    apiCreateApplication,
    apiDrivingOrganizationSchedulesDayFree,
    apiDrivingOrganizationSchedulesDayMonth,
    type ApplicationCreateDtoSchema,
    applicationCreateDtoSchema
} from "src/modules/exam-applications/shared";
import {useEffect, useMemo} from 'react';
import {toast} from "react-toastify";
import type {StudentDto} from "@/global/api/gen/ts/StudentDto";
import type {ApplicationAiTestCreateDto} from "@/global/api/gen/ts/ApplicationAiTestCreateDto";

const today = dayjs();

const schema = applicationCreateDtoSchema.extend({
    file: z.instanceof(File, { message: 'Файл не выбран' }),
    category: z.union([
        z.literal(1, { message: 'Выберете категорию' }),
        z.literal(2, { message: 'Выберете категорию' }),
        z.literal(3, { message: 'Выберете категорию' }),
        z.literal(4, { message: 'Выберете категорию' }),
        z.literal(5, { message: 'Выберете категорию' }),
        z.literal(6, { message: 'Выберете категорию' }),
        z.literal(8, { message: 'Выберете категорию' })]
    ),
    date: z.date({ message: 'Выберете дату' }).min(today.toDate(), { message: 'Выберете дату' }),
    time: z.string({ message: 'Выберете время' }).min(0, { message: 'Выберете время' })
});

interface Options {
    studentInfo?: StudentDto,
    afterSubmit?: () => void
}

const CategoryAisMapping: Record<string, ApplicationCreateDtoSchema['category']> = {
    A: 1,
    B: 2,
    C: 3,
    D: 4,
    CD: 5,
    TM: 6,
    BC: 8,
};

export const useRegisterApplication = ({studentInfo, afterSubmit}:Options) => {
    const { mutateAsync, isPending } = apiCreateApplication();

    const {
        handleSubmit,
        control,
        clearErrors,
        reset,
        watch,
        setValue,
        resetField
    } = useForm<ApplicationCreateDtoSchema>({
        resolver: zodResolver(schema),
        defaultValues: {
            firstName: studentInfo?.first_name,
            lastName: studentInfo?.last_name,
            patronymic: studentInfo?.patronymic,
            iin: studentInfo?.iin,
            category: studentInfo?.group?.category_id ?  CategoryAisMapping[studentInfo?.group?.category_id] ?? undefined : undefined
        }
    });

    const organizationId = watch('organizationId');

    const date = watch('date');

    const formattedDate = useMemo(() => {
        if (!date) {
            return null;
        }
        return dayjs(date).format('YYYY-MM-DD');
    }, [date]);
    const formattedForMonth = useMemo(() => {
        if (!date) {
            return dayjs(new Date()).format('YYYY-MM-DD');
        }
        return dayjs(date).format('YYYY-MM-DD');
    }, [date]);

    useEffect(() => {
        resetField('time');
    }, [setValue, date]);

    const {
        data: scheduleData,
        isLoading: isScheduleDataLoading
    } = apiDrivingOrganizationSchedulesDayFree({
        service_id: organizationId,
        date: formattedDate
    });

    const {
        data: scheduleMonth,
        isLoading: isScheduleMonthLoading
    } = apiDrivingOrganizationSchedulesDayMonth({
        organizationId: organizationId,
        date: formattedForMonth,
    });

    const availableDays = useMemo(() => {
        if (!scheduleMonth || !scheduleMonth.schedule) {
            return [];
        }
        return Object.keys(scheduleMonth.schedule).filter((date) => scheduleMonth.schedule[date]).map((date) => dayjs(date));
    }, [scheduleMonth]);

    const slots = useMemo(() => {
            if (!scheduleData) {
                return [];
            }
            // const set = new Set<string>(scheduleData.booked_times);
            const slots: { time: string, local: string, disabled: boolean }[] = [];

            scheduleData.available_times.map((time) => {
                const timeStr = `${time}:00`;
                const localTime = dayjs(`${formattedDate}T${time}:00`).local();
                const local = localTime.format('HH:mm');
                slots.push({
                    time: timeStr,
                    local,
                    disabled: false
                });
            });

            return slots;
        },
        [scheduleData, date, formattedDate]);

    const onSubmit: SubmitHandler<ApplicationCreateDtoSchema> = async (data) => {
        try {
            const sendData = {
                ...data,
                patronymic: data.patronymic ?? '',
                date: data.date,
                groupId: studentInfo?.group?.id ?? 0,
                schoolId: studentInfo?.group?.school?.id ?? 0,
            };

            const formatData:Record<string, string | File> = {};
            (Object.keys(sendData) as (keyof ApplicationCreateDtoSchema)[]).forEach((key) => {
                const value = sendData[key];
                if (typeof value === 'string' || value instanceof File) {
                    formatData[key] = value;
                } else if (value instanceof Date) {
                    formatData[key] = value.toISOString();
                } else if (value !== null) {
                    formatData[key] = value.toString();
                }
            });
            await mutateAsync(formatData as unknown as ApplicationAiTestCreateDto);
            toast.success('Заявка отправлена');
            if (afterSubmit && typeof afterSubmit === 'function') {
                afterSubmit();
            }
            reset();
        } catch (e) {
            toast.error('Ошибка отправки заявки');
        }
    };

    return {
        control,
        isPending,
        clearErrors,
        onFormSubmit: handleSubmit(onSubmit),
        organizationId,
        isScheduleDataLoading,
        slots,
        availableDays,
        isScheduleMonthLoading
    };
};
